import React, { useState, useMemo, useEffect } from 'react';
import { Eye, ArrowUpDown } from 'lucide-react';
import { CustomModal } from '../../components/ui/CustomModal';
import { Pagination } from '../../components/ui/Pagination';
import { formatCurrency, formatDateForDisplay } from '../../utils/helpers';

const ITEMS_PER_PAGE = 20;

export const Customers = ({ orders }) => {
    const [sortConfig, setSortConfig] = useState({ key: 'totalSpent', direction: 'descending' });
    const [viewingCustomer, setViewingCustomer] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    
    const customerData = useMemo(() => {
        const customerMap = new Map();
        orders.forEach(order => {
            if (!order.phone || !Array.isArray(order.items)) return;

            const total = order.items.reduce((sum, item) => sum + item.price * item.quantity, 0);
            if (customerMap.has(order.phone)) {
                const existing = customerMap.get(order.phone);
                existing.orderCount += 1;
                existing.totalSpent += total;
                if (new Date(order.date) > new Date(existing.lastPurchaseDate)) {
                    existing.lastPurchaseDate = order.date;
                }
            } else {
                customerMap.set(order.phone, {
                    phone: order.phone,
                    name: order.customerName,
                    orderCount: 1,
                    totalSpent: total,
                    lastPurchaseDate: order.date
                });
            }
        });

        return Array.from(customerMap.values()).map(c => ({
            ...c,
            daysSinceLastPurchase: Math.floor((new Date() - new Date(c.lastPurchaseDate)) / (1000 * 60 * 60 * 24))
        }));
    }, [orders]);

    useEffect(() => {
        setCurrentPage(1);
    }, [customerData]);

    const sortedCustomers = useMemo(() => {
        let sortableItems = [...customerData];
        if (sortConfig.key !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableItems;
    }, [customerData, sortConfig]);

    const paginatedCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return sortedCustomers.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [sortedCustomers, currentPage]);


    const customerOrders = useMemo(() => {
        if (!viewingCustomer) return [];
        return orders.filter(order => order.phone === viewingCustomer.phone);
    }, [viewingCustomer, orders]);

    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    const getSortIcon = (name) => {
        if (sortConfig.key !== name) {
            return <ArrowUpDown size={14} className="inline-block ml-1 opacity-40" />;
        }
        return sortConfig.direction === 'ascending' ? <span className="ml-1">▲</span> : <span className="ml-1">▼</span>;
    };

    return (
         <div className="space-y-6">
            <h2 className="text-3xl font-bold text-gray-800">Dữ liệu Khách hàng</h2>
             <CustomModal isOpen={!!viewingCustomer} onClose={() => setViewingCustomer(null)} title={`Lịch sử mua hàng: ${viewingCustomer?.name}`} maxWidth="max-w-6xl">
                 <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="border-b bg-gray-50">
                            <tr>
                                {['Mã Đơn', 'Ngày Đặt', 'Sản phẩm đã mua', 'Tổng Tiền', 'Trạng Thái'].map(h => <th key={h} className="p-3 font-semibold text-gray-600">{h}</th>)}
                            </tr>
                        </thead>
                         <tbody>
                            {customerOrders.map(order => (
                                <tr key={order.id} className="border-b">
                                    <td className="p-3 font-medium text-blue-600 align-top">{order.id}</td>
                                    <td className="p-3 align-top">{formatDateForDisplay(order.date)}</td>
                                    <td className="p-3 align-top">
                                        <ul className="list-disc list-inside text-sm">
                                            {order.items.map((item, index) => (
                                                <li key={index}>{item.productName} (x{item.quantity})</li>
                                            ))}
                                        </ul>
                                    </td>
                                    <td className="p-3 align-top">{formatCurrency(order.items.reduce((s,i) => s + i.price * i.quantity, 0))}</td>
                                    <td className="p-3 align-top">{order.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                 </div>
             </CustomModal>
             <div className="bg-white rounded-lg shadow-md">
                <div className="overflow-x-auto p-6">
                       <table className="w-full text-left">
                            <thead className="border-b bg-gray-50">
                                <tr>
                                    <th className="p-4 font-semibold text-gray-600">Hành động</th>
                                    <th className="p-4 font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort('phone')}>Số điện thoại {getSortIcon('phone')}</th>
                                    <th className="p-4 font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort('name')}>Tên Khách Hàng {getSortIcon('name')}</th>
                                    <th className="p-4 font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort('totalSpent')}>Tổng chi tiêu {getSortIcon('totalSpent')}</th>
                                    <th className="p-4 font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort('orderCount')}>Số đơn hàng {getSortIcon('orderCount')}</th>
                                    <th className="p-4 font-semibold text-gray-600 cursor-pointer" onClick={() => requestSort('daysSinceLastPurchase')}>Chưa mua lại (ngày) {getSortIcon('daysSinceLastPurchase')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCustomers.map(customer => (
                                    <tr key={customer.phone} className="border-b hover:bg-gray-50">
                                        <td className="p-4">
                                            <button onClick={() => setViewingCustomer(customer)} className="p-2 text-gray-500 hover:text-blue-600"><Eye size={18}/></button>
                                        </td>
                                        <td className="p-4 font-medium text-blue-600">{customer.phone}</td>
                                        <td className="p-4">{customer.name}</td>
                                        <td className="p-4">{formatCurrency(customer.totalSpent)}</td>
                                        <td className="p-4 text-center">{customer.orderCount}</td>
                                        <td className="p-4 text-center">{customer.daysSinceLastPurchase}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                </div>
                 <Pagination
                    currentPage={currentPage}
                    totalItems={sortedCustomers.length}
                    itemsPerPage={ITEMS_PER_PAGE}
                    onPageChange={setCurrentPage}
                />
            </div>
        </div>
    )
};
